import type { ValidationError } from '@angular/forms/signals';
import { CursorInputState, RangeCursorInputState } from '../number-field.models';

/**
 * The number mask as pure functions over the text and the caret: the component cancels every
 * native edit and applies one of these instead, so the thousands spaces and the caret stay
 * consistent. Values here carry no minus sign and, between `modifySpacesRemove` and
 * `modifySpacesAdd`, no spaces.
 */

export const MAX_PERCENT = 100;
export const MINUS_SIGN = '-';
const DECIMAL_SEPARATOR = '.';
const THOUSANDS_SEPARATOR = ' ';
const ZERO_DIGIT = '0';
const DIGITS_PER_THOUSANDS_GROUP = 3;

/** `kind`s of the errors `validateNumber` reports. */
export const NumberValidationErrorKind = {
  Zero: 'zero',
  Min: 'min',
  Max: 'max',
  MaxLength: 'maxLength',
} as const;
export type NumberValidationErrorKind =
  (typeof NumberValidationErrorKind)[keyof typeof NumberValidationErrorKind];

export interface NumberInputOptions {
  fractional: number;
  maxIntegerDigits?: number;
  isPercent: boolean;
}

export interface NumberDisplayOptions {
  fractional: number;
  fractionalMin: number;
  skipZero: boolean;
}

export interface NumberValidationOptions {
  required: boolean;
  zeroAble: boolean;
  isPercent: boolean;
  min?: string | number;
  max?: string | number;
  maxIntegerDigits?: number;
}

export const isSeparator = (char: string): boolean =>
  ['.', ',', '/', '?'].includes(char.toLowerCase());

export const isDigit = (char: string): boolean => /^\d$/.test(char);

export const removePrefixZeros = (value: string): string => value.replace(/^0+(?=\d)/, '');

export const removeTrailingZeros = (value: string): string =>
  value.replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.0+$/, '');

export function formatNumberWithSpaces(value: string): string {
  const [intPart = '', fracPart] = value.split('.');
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  return fracPart !== undefined ? `${formattedInt}.${fracPart}` : formattedInt;
}

/** The text shown once the field is left: padded to `fractionalMin`, grouped by thousands. */
export function formatForDisplay(input: string, options: NumberDisplayOptions): string {
  let value = input === MINUS_SIGN ? '' : input;

  if (!value) {
    if (options.fractional > 0 || options.skipZero) {
      return value;
    }

    value = '0';
  }

  value = removeTrailingZeros(value);

  if (value[0] === MINUS_SIGN && parseFloat(value.substring(1)) === 0) {
    value = value.substring(1);
  }

  const separatorIndex = value.indexOf('.');
  const { fractionalMin } = options;

  if (separatorIndex !== -1) {
    const fractionDigits = value.length - separatorIndex - 1;

    if (fractionalMin === 0 && fractionDigits === 0) {
      value = value.replace('.', '');
    } else if (fractionalMin > fractionDigits) {
      value = value + '0'.repeat(fractionalMin - fractionDigits);
    }
  } else if (fractionalMin > 0) {
    value = value + '.' + '0'.repeat(fractionalMin);
  }

  return formatNumberWithSpaces(value);
}

/** The number string the model receives for the text on screen. */
export function cleanNumber(raw: string): string {
  let cleaned = removeTrailingZeros(raw.replaceAll(' ', ''));

  if (cleaned[cleaned.length - 1] === DECIMAL_SEPARATOR) {
    cleaned = cleaned.slice(0, -1);
  }

  return cleaned === MINUS_SIGN ? '' : cleaned;
}

export function validateNumber(
  raw: string,
  options: NumberValidationOptions,
): ValidationError.WithoutFieldTree | undefined {
  const valStr = raw.replaceAll(' ', '');
  const valNum = parseFloat(valStr);
  const { min, max, maxIntegerDigits } = options;

  if (valStr && options.required && !options.zeroAble && valNum === 0) {
    return { kind: NumberValidationErrorKind.Zero };
  }

  if (min !== undefined && min !== null && valNum < parseFloat(min.toString())) {
    return { kind: NumberValidationErrorKind.Min };
  }

  if (
    (max !== undefined && max !== null && valNum > parseFloat(max.toString())) ||
    (options.isPercent && valNum > MAX_PERCENT)
  ) {
    return { kind: NumberValidationErrorKind.Max };
  }

  if (valStr && maxIntegerDigits) {
    const [integerPart = ''] = valStr.split('.');
    const integerDigits = integerPart.replaceAll('-', '');

    if (integerDigits.length > maxIntegerDigits) {
      return { kind: NumberValidationErrorKind.MaxLength };
    }
  }

  return undefined;
}

export function checkPercentExceeds(
  prevState: CursorInputState,
  newState: CursorInputState,
  isPercent: boolean,
): CursorInputState {
  return isPercent && parseFloat(newState.value) > MAX_PERCENT ? prevState : newState;
}

export function modifySpacesRemove(
  cursor: Partial<CursorInputState & RangeCursorInputState> & { value: string },
): CursorInputState & RangeCursorInputState {
  let cleanedValue = '';
  let removedBeforeStart = 0;
  let removedBeforeEnd = 0;

  for (let i = 0; i < cursor.value.length; i++) {
    const isSpace = cursor.value[i] === THOUSANDS_SEPARATOR;

    if (
      ((cursor.cursorStart && i < cursor.cursorStart) ||
        (cursor.cursorIndex && i < cursor.cursorIndex)) &&
      isSpace
    )
      removedBeforeStart++;
    if (cursor.cursorEnd !== undefined && i < cursor.cursorEnd && isSpace) removedBeforeEnd++;

    if (!isSpace) cleanedValue += cursor.value[i];
  }

  return {
    value: cleanedValue,
    cursorStart: (cursor.cursorStart ?? 0) - removedBeforeStart,
    cursorEnd: (cursor.cursorEnd ?? 0) - removedBeforeEnd,
    cursorIndex: (cursor.cursorIndex ?? 0) - removedBeforeStart,
  };
}

export function modifySpacesAdd(state: CursorInputState): CursorInputState {
  const [intPart = '', fracPart] = state.value.split('.');
  let formattedInt = '';
  let cursorIndexDelta = 0;
  const startIndex = (intPart.length + 2) % DIGITS_PER_THOUSANDS_GROUP;

  for (let i = 0; i < intPart.length; i++) {
    formattedInt += intPart.charAt(i);

    if ((i - startIndex) % DIGITS_PER_THOUSANDS_GROUP === 0 && i !== intPart.length - 1) {
      formattedInt += ' ';

      if (state.cursorIndex > i + 1) {
        cursorIndexDelta += 1;
      }
    }
  }

  return {
    value: fracPart !== undefined ? `${formattedInt}.${fracPart}` : formattedInt,
    cursorIndex: state.cursorIndex + cursorIndexDelta,
  };
}

export function processDeleteForward(state: RangeCursorInputState): CursorInputState {
  const separatorIndex = state.value.indexOf('.');
  const [intPart] = state.value.split('.');

  if (state.cursorStart !== state.cursorEnd) {
    return {
      value: state.value.substring(0, state.cursorStart) + state.value.substring(state.cursorEnd),
      cursorIndex: state.cursorStart,
    };
  }

  if (separatorIndex === 1 && state.cursorStart === 0) {
    return { value: '0' + state.value.substring(1), cursorIndex: state.cursorStart };
  }

  if (intPart === ZERO_DIGIT && state.cursorStart === 1) {
    return { value: removePrefixZeros(state.value.substring(2)), cursorIndex: 0 };
  }

  if (state.cursorStart === 0) {
    return { value: removePrefixZeros(state.value.substring(1)), cursorIndex: 0 };
  }

  return {
    value:
      state.value.substring(0, state.cursorStart) + state.value.substring(state.cursorStart + 1),
    cursorIndex: state.cursorStart,
  };
}

export function processDeleteBackward(state: RangeCursorInputState): CursorInputState {
  const separatorIndex = state.value.indexOf('.');
  const [intPart] = state.value.split('.');

  if (state.cursorStart !== state.cursorEnd) {
    return {
      value: state.value.substring(0, state.cursorStart) + state.value.substring(state.cursorEnd),
      cursorIndex: state.cursorStart,
    };
  }

  if (separatorIndex === 1 && state.cursorStart === 1) {
    return { value: '0' + state.value.substring(1), cursorIndex: state.cursorStart };
  }

  if (intPart === ZERO_DIGIT && state.cursorStart === 2) {
    return { value: removePrefixZeros(state.value.substring(2)), cursorIndex: 0 };
  }

  if (state.cursorStart === 1) {
    return { value: removePrefixZeros(state.value.substring(1)), cursorIndex: 0 };
  }

  return {
    value:
      state.value.substring(0, state.cursorStart - 1) + state.value.substring(state.cursorStart),
    cursorIndex: state.cursorStart - 1,
  };
}

export function processSymbol(
  symbol: string,
  state: CursorInputState,
  options: NumberInputOptions,
): CursorInputState {
  const { fractional } = options;

  if (isSeparator(symbol)) {
    if (fractional === 0) return state;

    if (state.value.indexOf('.') === state.cursorIndex) {
      return { value: state.value, cursorIndex: state.cursorIndex + 1 };
    }

    if (!state.value.includes('.') && state.cursorIndex >= state.value.length - fractional) {
      if (state.cursorIndex === 0) {
        return { value: '0.' + state.value, cursorIndex: 2 };
      }

      return {
        value:
          state.value.substring(0, state.cursorIndex) +
          '.' +
          state.value.substring(state.cursorIndex),
        cursorIndex: state.cursorIndex + 1,
      };
    }

    return state;
  }

  if (!isDigit(symbol)) return state;

  const separatorIndex = state.value.indexOf('.');
  const [intPart = '', fracPart = ''] = state.value.split('.');
  const maxIntegerDigits = options.maxIntegerDigits ?? Infinity;

  if (separatorIndex !== -1 && state.cursorIndex > separatorIndex) {
    if (fracPart.length < fractional) {
      return {
        value:
          state.value.substring(0, state.cursorIndex) +
          symbol +
          state.value.substring(state.cursorIndex),
        cursorIndex: state.cursorIndex + 1,
      };
    }

    if (state.cursorIndex === state.value.length) return state;

    return {
      value:
        state.value.substring(0, state.cursorIndex) +
        symbol +
        state.value.substring(state.cursorIndex + 1),
      cursorIndex: state.cursorIndex + 1,
    };
  }

  if (symbol === ZERO_DIGIT && state.cursorIndex === 0 && state.value.length !== 0) {
    return state;
  }

  if (
    intPart.length >= maxIntegerDigits ||
    (options.isPercent && parseInt(intPart + symbol) > MAX_PERCENT)
  ) {
    if (state.cursorIndex === intPart.length) {
      if (fractional === 0) return state;

      return state.value.includes('.')
        ? {
            value: intPart + '.' + symbol + fracPart.substring(1),
            cursorIndex: state.cursorIndex + 2,
          }
        : { value: state.value + '.' + symbol, cursorIndex: state.cursorIndex + 2 };
    }

    return {
      value:
        state.value.substring(0, state.cursorIndex) +
        symbol +
        state.value.substring(state.cursorIndex + 1),
      cursorIndex: state.cursorIndex + 1,
    };
  }

  if (intPart === ZERO_DIGIT && state.cursorIndex === 1) {
    return { value: symbol + state.value.substring(1), cursorIndex: state.cursorIndex };
  }

  return {
    value:
      state.value.substring(0, state.cursorIndex) +
      symbol +
      state.value.substring(state.cursorIndex),
    cursorIndex: state.cursorIndex + 1,
  };
}
